export function toSearchParams(
  values: Record<string, string | number | boolean | readonly string[] | undefined>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === '') {
      continue;
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry !== undefined && entry !== '') {
          params.append(key, String(entry));
        }
      }
      continue;
    }
    params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function formatDate(isoDate: string | null): string {
  if (!isoDate) {
    return '—';
  }
  const date = isoDate.length <= 10 ? `${isoDate}T00:00:00.000Z` : isoDate;
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' }).format(new Date(date));
}

export function formatDateTime(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

/** Format an instant in a fixed IANA zone (e.g. Change calendar Europe/Berlin). */
export function formatDateTimeInZone(iso: string | null, timeZone: string): string {
  if (!iso) {
    return '—';
  }
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone,
  }).format(new Date(iso));
}

export function formatSlaMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours > 0 && rest > 0) {
    return `${hours} Std. ${rest} Min.`;
  }
  if (hours > 0) {
    return `${hours} Std.`;
  }
  return `${minutes} Min.`;
}

export function formatCountdown(ms: number): string {
  const overdue = ms < 0;
  const totalSec = Math.floor(Math.abs(ms) / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  const body = `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return overdue ? `−${body}` : body;
}

export function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromDatetimeLocalValue(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
}
